import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  Card,
  CardContent,
} from "@mui/material";
import {
  Assessment as ReportIcon,
  Receipt as TaxReportIcon,
  ShoppingCart as SalesIcon,
  Payment as PaymentIcon,
  People as CustomerIcon,
  AccountBalance as VATIcon,
  Search as SearchIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  History as HistoryIcon,
  Clear as ClearIcon,
  TrendingUp as SummaryIcon,
  Description as QuotationIcon,
  ReceiptLong as VATDetailIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../hooks/useTranslation";
import ReportForm from "./ReportForm";

// Sample data for demonstration
const sampleData = {
  vatPurchase: [
    {
      id: 1,
      date: "2023-01-01",
      supplier: "ABC Suppliers",
      invoice: "PINV-001",
      amount: 10000,
      vat: 1500,
    },
    {
      id: 2,
      date: "2023-01-02",
      supplier: "XYZ Trading",
      invoice: "PINV-002",
      amount: 5000,
      vat: 750,
    },
  ],
  vatSales: [
    {
      id: 1,
      date: "2023-01-01",
      customer: "Customer A",
      invoice: "INV-001",
      amount: 15000,
      vat: 2250,
    },
    {
      id: 2,
      date: "2023-01-02",
      customer: "Customer B",
      invoice: "INV-002",
      amount: 8000,
      vat: 1200,
    },
  ],
  vatReturn: [
    {
      id: 1,
      period: "Q1 2023",
      salesVAT: 5000,
      purchaseVAT: 3000,
      netVAT: 2000,
      status: "Payable",
    },
    {
      id: 2,
      period: "Q2 2023",
      salesVAT: 6000,
      purchaseVAT: 4000,
      netVAT: 2000,
      status: "Payable",
    },
  ],
  salesSummary: [
    {
      id: 1,
      date: "2023-01-01",
      invoices: 15,
      amount: 45000,
      vat: 6750,
      total: 51750,
    },
    {
      id: 2,
      date: "2023-01-02",
      invoices: 12,
      amount: 38000,
      vat: 5700,
      total: 43700,
    },
  ],
  paymentCollection: [
    {
      id: 1,
      date: "2023-01-01",
      customer: "Customer A",
      invoice: "INV-001",
      amount: 15000,
      mode: "Bank Transfer",
    },
    {
      id: 2,
      date: "2023-01-02",
      customer: "Customer B",
      invoice: "INV-002",
      amount: 8000,
      mode: "Cash",
    },
  ],
  customerSales: [
    {
      id: 1,
      customer: "Customer A",
      invoices: 25,
      amount: 125000,
      vat: 18750,
      total: 143750,
    },
    {
      id: 2,
      customer: "Customer B",
      invoices: 18,
      amount: 90000,
      vat: 13500,
      total: 103500,
    },
  ],
  quotationReport: [
    {
      id: 1,
      quotationNo: "QT-0001",
      date: "2023-01-01",
      customer: "Customer A",
      validUntil: "2023-02-01",
      amount: 15000,
      vat: 2250,
      total: 17250,
      status: "draft",
    },
    {
      id: 2,
      quotationNo: "QT-0002",
      date: "2023-01-02",
      customer: "Customer B",
      validUntil: "2023-02-02",
      amount: 8000,
      vat: 1200,
      total: 9200,
      status: "sent",
    },
  ],
};

const columnHeaderKeyMap = {
  Date: "Date",
  Supplier: "Supplier",
  Customer: "Customer",
  Invoice: "Invoice",
  Amount: "Amount",
  "VAT Paid": "VAT_Paid",
  "VAT Collected": "VAT_Collected",
  Period: "Period",
  "Sales VAT": "Sales_VAT",
  "Purchase VAT": "Purchase_VAT",
  "Net VAT": "Net_VAT",
  Status: "Status",
  Invoices: "Invoices",
  VAT: "VAT",
  Total: "Total",
  "Payment Mode": "Payment_Mode",
  "Quotation #": "Quotation_Number",
  "Valid Until": "Valid_Until",
};

const reportColumns = {
  vatPurchase: [
    { field: "date", headerName: "Date", headerKey: "Date", width: "15%" },
    {
      field: "supplier",
      headerName: "Supplier",
      headerKey: "Supplier",
      width: "25%",
    },
    {
      field: "invoice",
      headerName: "Invoice",
      headerKey: "Invoice",
      width: "15%",
    },
    {
      field: "amount",
      headerName: "Amount",
      headerKey: "Amount",
      width: "20%",
      align: "right",
    },
    {
      field: "vat",
      headerName: "VAT Paid",
      headerKey: "VAT_Paid",
      width: "15%",
      align: "right",
    },
  ],
  vatSales: [
    { field: "date", headerName: "Date", headerKey: "Date", width: "15%" },
    {
      field: "customer",
      headerName: "Customer",
      headerKey: "Customer",
      width: "25%",
    },
    {
      field: "invoice",
      headerName: "Invoice",
      headerKey: "Invoice",
      width: "15%",
    },
    {
      field: "amount",
      headerName: "Amount",
      headerKey: "Amount",
      width: "20%",
      align: "right",
    },
    {
      field: "vat",
      headerName: "VAT Collected",
      headerKey: "VAT_Collected",
      width: "15%",
      align: "right",
    },
  ],
  vatReturn: [
    {
      field: "period",
      headerName: "Period",
      headerKey: "Period",
      width: "20%",
    },
    {
      field: "salesVAT",
      headerName: "Sales VAT",
      headerKey: "Sales_VAT",
      width: "20%",
      align: "right",
    },
    {
      field: "purchaseVAT",
      headerName: "Purchase VAT",
      headerKey: "Purchase_VAT",
      width: "20%",
      align: "right",
    },
    {
      field: "netVAT",
      headerName: "Net VAT",
      headerKey: "Net_VAT",
      width: "20%",
      align: "right",
    },
    {
      field: "status",
      headerName: "Status",
      headerKey: "Status",
      width: "15%",
    },
  ],
  salesSummary: [
    { field: "date", headerName: "Date", headerKey: "Date", width: "20%" },
    {
      field: "invoices",
      headerName: "Invoices",
      headerKey: "Invoices",
      width: "15%",
      align: "right",
    },
    {
      field: "amount",
      headerName: "Amount",
      headerKey: "Amount",
      width: "20%",
      align: "right",
    },
    {
      field: "vat",
      headerName: "VAT",
      headerKey: "VAT",
      width: "20%",
      align: "right",
    },
    {
      field: "total",
      headerName: "Total",
      headerKey: "Total",
      width: "20%",
      align: "right",
    },
  ],
  paymentCollection: [
    { field: "date", headerName: "Date", headerKey: "Date", width: "15%" },
    {
      field: "customer",
      headerName: "Customer",
      headerKey: "Customer",
      width: "25%",
    },
    {
      field: "invoice",
      headerName: "Invoice",
      headerKey: "Invoice",
      width: "15%",
    },
    {
      field: "amount",
      headerName: "Amount",
      headerKey: "Amount",
      width: "20%",
      align: "right",
    },
    {
      field: "mode",
      headerName: "Payment Mode",
      headerKey: "Payment_Mode",
      width: "20%",
    },
  ],
  customerSales: [
    {
      field: "customer",
      headerName: "Customer",
      headerKey: "Customer",
      width: "30%",
    },
    {
      field: "invoices",
      headerName: "Invoices",
      headerKey: "Invoices",
      width: "15%",
      align: "right",
    },
    {
      field: "amount",
      headerName: "Amount",
      headerKey: "Amount",
      width: "20%",
      align: "right",
    },
    {
      field: "vat",
      headerName: "VAT",
      headerKey: "VAT",
      width: "15%",
      align: "right",
    },
    {
      field: "total",
      headerName: "Total",
      headerKey: "Total",
      width: "20%",
      align: "right",
    },
  ],
  quotationReport: [
    {
      field: "quotationNo",
      headerName: "Quotation #",
      headerKey: "Quotation_Number",
      width: "12%",
    },
    { field: "date", headerName: "Date", headerKey: "Date", width: "12%" },
    {
      field: "customer",
      headerName: "Customer",
      headerKey: "Customer",
      width: "20%",
    },
    {
      field: "validUntil",
      headerName: "Valid Until",
      headerKey: "Valid_Until",
      width: "12%",
    },
    {
      field: "amount",
      headerName: "Amount",
      headerKey: "Amount",
      width: "13%",
      align: "right",
    },
    {
      field: "vat",
      headerName: "VAT",
      headerKey: "VAT",
      width: "13%",
      align: "right",
    },
    {
      field: "total",
      headerName: "Total",
      headerKey: "Total",
      width: "13%",
      align: "right",
    },
    {
      field: "status",
      headerName: "Status",
      headerKey: "Status",
      width: "10%",
    },
  ],
};

const reportCategories = [
  {
    title: "VAT Reports",
    titleKey: "VAT_Reports",
    reports: [
      {
        name: "VAT Purchase Report",
        nameKey: "VAT_Purchase_Report",
        icon: <TaxReportIcon color="primary" />,
        type: "vatPurchase",
        title: "VAT Purchase Report",
        titleKey: "VAT_Purchase_Report",
        description: "Shows VAT paid on purchases",
        descriptionKey: "VAT_Paid_Description",
      },
      {
        name: "VAT Sales Report",
        nameKey: "VAT_Sales_Report",
        icon: <VATIcon color="primary" />,
        type: "vatSales",
        title: "VAT Sales Report",
        titleKey: "VAT_Sales_Report",
        description: "Shows VAT collected from customers",
        descriptionKey: "VAT_Collected_Description",
      },
      {
        name: "VAT Return Summary",
        nameKey: "VAT_Return_Summary",
        icon: <SummaryIcon color="primary" />,
        type: "vatReturn",
        title: "VAT Return Summary",
        titleKey: "VAT_Return_Summary",
        description: "Displays net VAT payable",
        descriptionKey: "Net_VAT_Description",
      },
      {
        name: "VAT Details",
        nameKey: "VAT_Detail",
        icon: <VATDetailIcon color="primary" />,
        type: "VAT_Detail",
        title: "VAT Details",
        titleKey: "VAT_Detail",
        description: "Comprehensive breakdown of sales and purchase VAT",
        descriptionKey: "VAT_Detail_Description",
      },
    ],
  },
  {
    title: "Sales & Payment Reports",
    titleKey: "Sales_Payment_Reports",
    reports: [
      {
        name: "DSR (Daily Sales Report)",
        nameKey: "DSR",
        icon: <ReportIcon color="primary" />,
        type: "dsr",
        title: "Daily Sales Report",
        titleKey: "Daily_Sales_Report",
        description: "Comprehensive daily sales breakdown with hourly analysis",
        descriptionKey: "DSR_Description",
      },
      {
        name: "Sales Summary Report",
        nameKey: "Sales_Summary_Report",
        icon: <SalesIcon color="primary" />,
        type: "salesSummary",
        title: "Sales Summary Report",
        titleKey: "Sales_Summary_Report",
        description: "Sales performance by day, month, or year",
        descriptionKey: "Sales_Summary_Description",
      },
      {
        name: "Payment Collection Report",
        nameKey: "Payment_Collection_Report",
        icon: <PaymentIcon color="primary" />,
        type: "paymentCollection",
        title: "Payment Collection Report",
        titleKey: "Payment_Collection_Report",
        description: "Tracks received payments by mode",
        descriptionKey: "Payment_Collection_Description",
      },
      {
        name: "Customer Sales Report",
        nameKey: "Customer_Sales_Report",
        icon: <CustomerIcon color="primary" />,
        type: "customerSales",
        title: "Customer Sales Report",
        titleKey: "Customer_Sales_Report",
        description: "Sales per customer",
        descriptionKey: "Customer_Sales_Description",
      },
      {
        name: "Quotation Report",
        nameKey: "Quotation_Report",
        icon: <QuotationIcon color="primary" />,
        type: "quotationReport",
        title: "Quotation Report",
        titleKey: "Quotation_Report",
        description: "All quotations with status and validity",
        descriptionKey: "Quotation_Description",
      },
    ],
  },
];

const ReportCard = ({
  report,
  onReportClick,
  isFavorite,
  onToggleFavorite,
  t,
  translateReportText,
}) => {
  const getDisplayName = () => {
    if (typeof translateReportText === "function") {
      return translateReportText(report);
    }
    if (typeof t === "function" && report.nameKey) {
      return t(`Reports.${report.nameKey}`);
    }
    return report.name;
  };

  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1.5,
        cursor: "pointer",
        transition: "all 0.2s",
        "&:hover": {
          boxShadow: 2,
          borderColor: "primary.main",
          transform: "translateY(-2px)",
        },
        "&:focus-within": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: "2px",
        },
      }}
      onClick={() => onReportClick(report)}
      tabIndex={0}
      role="button"
      onKeyPress={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          onReportClick(report);
        }
      }}
    >
      <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", mb: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 36,
              height: 36,
              borderRadius: 1,
              bgcolor: "primary.lighter",
              mr: 1.5,
            }}
          >
            {React.cloneElement(report.icon, { sx: { fontSize: 20 } })}
          </Box>
          <Box sx={{ flex: 1 }}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onToggleFavorite(report.type);
              }}
              sx={{
                float: "right",
                p: 0.5,
                color: isFavorite ? "warning.main" : "action.disabled",
                "&:hover": {
                  color: "warning.main",
                  bgcolor: "warning.lighter",
                },
              }}
            >
              {isFavorite ? (
                <StarIcon sx={{ fontSize: 18 }} />
              ) : (
                <StarBorderIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </Box>
        </Box>
        <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
          {getDisplayName()}
        </Typography>
      </CardContent>
    </Card>
  );
};

function Reports() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [selectedReport, setSelectedReport] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [favorites, setFavorites] = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);

  // Load favorites and recently viewed from localStorage
  useEffect(() => {
    const savedFavorites = JSON.parse(
      localStorage.getItem("reportFavorites") || "[]",
    );
    const savedRecent = JSON.parse(
      localStorage.getItem("recentlyViewedReports") || "[]",
    );
    setFavorites(savedFavorites);
    setRecentlyViewed(savedRecent);
  }, []);

  // Save favorites to localStorage
  const toggleFavorite = (reportType) => {
    const newFavorites = favorites.includes(reportType)
      ? favorites.filter((f) => f !== reportType)
      : [...favorites, reportType];
    setFavorites(newFavorites);
    localStorage.setItem("reportFavorites", JSON.stringify(newFavorites));
  };

  // Add to recently viewed
  const addToRecentlyViewed = (report) => {
    const newRecent = [
      report.type,
      ...recentlyViewed.filter((r) => r !== report.type),
    ].slice(0, 5); // Keep only 5 most recent
    setRecentlyViewed(newRecent);
    localStorage.setItem("recentlyViewedReports", JSON.stringify(newRecent));
  };

  const handleReportClick = (report) => {
    addToRecentlyViewed(report);
    setSelectedReport(report);
    navigate(`/reports/${report.type}`);
  };

  const translateReportText = (report) => {
    return report.nameKey ? t(`Reports.${report.nameKey}`) : report.name;
  };

  const getTranslatedColumns = (columns) => {
    return (columns || []).map((col) => {
      const key = col.headerKey || columnHeaderKeyMap[col.headerName];
      return {
        ...col,
        headerName: key ? t(`Reports.${key}`) : col.headerName,
      };
    });
  };

  // Filter reports based on search query
  const trimmedQuery = searchQuery.trim().toLowerCase();
  const filteredCategories = reportCategories
    .map((category) => {
      const reports = category.reports.filter((report) => {
        if (!trimmedQuery) return true;

        const translatedName = report.nameKey
          ? t(`Reports.${report.nameKey}`).toLowerCase()
          : "";
        const translatedTitle = report.titleKey
          ? t(`Reports.${report.titleKey}`).toLowerCase()
          : "";
        const translatedDesc = report.descriptionKey
          ? t(`Reports.${report.descriptionKey}`).toLowerCase()
          : "";

        const rawName = (report.name || "").toLowerCase();
        const rawTitle = (report.title || "").toLowerCase();
        const rawDesc = (report.description || "").toLowerCase();

        return (
          translatedName.includes(trimmedQuery) ||
          translatedTitle.includes(trimmedQuery) ||
          translatedDesc.includes(trimmedQuery) ||
          rawName.includes(trimmedQuery) ||
          rawTitle.includes(trimmedQuery) ||
          rawDesc.includes(trimmedQuery)
        );
      });

      return {
        ...category,
        reports,
      };
    })
    .filter((category) => category.reports.length > 0);

  // Get all reports as flat array for favorites/recent
  const allReports = reportCategories.flatMap((cat) => cat.reports);
  const favoriteReports = allReports.filter((r) => favorites.includes(r.type));
  const recentReports = recentlyViewed
    .map((type) => allReports.find((r) => r.type === type))
    .filter(Boolean);

  if (selectedReport) {
    const translatedTitle = selectedReport.titleKey
      ? t(`Reports.${selectedReport.titleKey}`)
      : selectedReport.title;

    return (
      <ReportForm
        title={translatedTitle}
        columns={getTranslatedColumns(reportColumns[selectedReport.type])}
        data={sampleData[selectedReport.type] || []}
      />
    );
  }

  return (
    <Container
      maxWidth={false}
      sx={{ mt: 0.5, px: { xs: 0.5, sm: 1, md: 1, lg: 1 } }}
    >
      {/* Header */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="h5" component="h1" fontWeight="700" gutterBottom>
          {t("Reports.Reports")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("Reports.Description")}
        </Typography>

        {/* Search Bar */}
        <TextField
          fullWidth
          placeholder={t("Reports.Search_Reports")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" sx={{ fontSize: "1.1rem" }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery("")}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          size="small"
          sx={{
            maxWidth: 500,
            "& .MuiOutlinedInput-root": {
              bgcolor: "background.paper",
              borderRadius: 0,
              height: "36px",
              fontSize: "0.875rem",
              "&:hover": {
                bgcolor: "background.paper",
              },
            },
            "& .MuiOutlinedInput-input": {
              padding: "8px 14px",
              height: "36px",
              boxSizing: "border-box",
            },
          }}
        />
      </Box>

      {/* Favorites Section */}
      {favoriteReports.length > 0 && !searchQuery && (
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
            <StarIcon sx={{ mr: 0.75, fontSize: 20, color: "warning.main" }} />
            <Typography variant="subtitle1" fontWeight="600">
              {t("Reports.Favorites")}
            </Typography>
          </Box>
          <Grid container spacing={1}>
            {favoriteReports.map((report) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={report.type}>
                <ReportCard
                  report={report}
                  onReportClick={handleReportClick}
                  isFavorite={favorites.includes(report.type)}
                  onToggleFavorite={toggleFavorite}
                  t={t}
                  translateReportText={translateReportText}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Recently Viewed Section */}
      {recentReports.length > 0 && !searchQuery && (
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
            <HistoryIcon sx={{ mr: 0.75, fontSize: 20, color: "info.main" }} />
            <Typography variant="subtitle1" fontWeight="600">
              {t("Reports.Recently_Viewed")}
            </Typography>
          </Box>
          <Grid container spacing={1}>
            {recentReports.map((report) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={report.type}>
                <ReportCard
                  report={report}
                  onReportClick={handleReportClick}
                  isFavorite={favorites.includes(report.type)}
                  onToggleFavorite={toggleFavorite}
                  t={t}
                  translateReportText={translateReportText}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* All Reports by Category */}
      {searchQuery && filteredCategories.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <SearchIcon sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {t("Reports.No_Reports_Found")} "{searchQuery}"
          </Typography>
        </Box>
      ) : (
        <Box>
          {!searchQuery && (
            <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2 }}>
              {t("Reports.All_Reports")}
            </Typography>
          )}
          <Grid container spacing={1}>
            {filteredCategories.map((category, index) => (
              <Grid item xs={12} lg={6} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1.5,
                    transition: "all 0.2s",
                    "&:hover": {
                      boxShadow: 2,
                      borderColor: "primary.main",
                    },
                  }}
                >
                  <Box
                    sx={{
                      p: 1.5,
                      background:
                        "linear-gradient(135deg, rgba(67, 97, 238, 0.05) 0%, rgba(67, 97, 238, 0.02) 100%)",
                      borderBottom: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight="700"
                      color="primary.main"
                    >
                      {t(`Reports.${category.titleKey}`)}
                    </Typography>
                  </Box>
                  <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                    <Stack spacing={1}>
                      {category.reports.map((report, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            p: 1,
                            borderRadius: 1,
                            cursor: "pointer",
                            border: "1px solid transparent",
                            transition: "all 0.2s",
                            "&:hover": {
                              bgcolor: "rgba(67, 97, 238, 0.08)",
                              borderColor: "primary.main",
                              transform: "translateX(4px)",
                            },
                            "&:focus-within": {
                              outline: "2px solid",
                              outlineColor: "primary.main",
                              outlineOffset: "2px",
                            },
                          }}
                          onClick={() => handleReportClick(report)}
                          tabIndex={0}
                          role="button"
                          onKeyPress={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              handleReportClick(report);
                            }
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: 32,
                              height: 32,
                              borderRadius: 1,
                              bgcolor: "primary.lighter",
                              mr: 1.5,
                              flexShrink: 0,
                            }}
                          >
                            {React.cloneElement(report.icon, {
                              sx: { fontSize: 18 },
                            })}
                          </Box>
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            sx={{ flex: 1 }}
                          >
                            {t(`Reports.${report.nameKey}`)}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFavorite(report.type);
                            }}
                            sx={{
                              p: 0.5,
                              color: favorites.includes(report.type)
                                ? "warning.main"
                                : "action.disabled",
                              "&:hover": {
                                color: "warning.main",
                                bgcolor: "warning.lighter",
                              },
                            }}
                          >
                            {favorites.includes(report.type) ? (
                              <StarIcon sx={{ fontSize: 18 }} />
                            ) : (
                              <StarBorderIcon sx={{ fontSize: 18 }} />
                            )}
                          </IconButton>
                        </Box>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Container>
  );
}

export default Reports;
